import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import { doTrialSignup } from "@/services/service-layer/authService";
import { BASE_URL } from "@/utilities/configurations";
import OpticksCheckbox from "@/components/ui/OpticksCheckbox/OpticksCheckbox";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import { getTimezone } from "@/utilities/utils";
import OpticksLogo from "@/components/ui/OpticksLogos/OpticksLogo";
import OpticksSpinner from "@/components/ui/OpticksSpinner/OpticksSpinner";
import { Controller, useForm } from "react-hook-form";
import { useLoaderStore } from "@/zustand/loaderStore";
import OpticksPlanPillIndicator from "@/components/ui/OpticksPlanPillIndicator/OpticksPlanPillIndicator";
import { PLAN_MODE } from "@/utilities/constants";
import storageService from "@/services/middleware/storageService";
import "./GoogleSignup.css";

interface GoogleSignupFormValues {
  email: string;
  firstName: string;
  companyName: string;
  terms: boolean;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split(".")[1];
  if (!base64Url) throw new Error("Invalid JWT token");
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(jsonPayload);
}

const GoogleSignup = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const referrerParam = searchParams.get("referrer");
  const referrer = (() => {
    if (referrerParam) {
      storageService.setItem("referrer", referrerParam);
      return referrerParam;
    }
    return storageService.getItem("referrer") || undefined;
  })();
  const { loading: isLoading, setLoading } = useLoaderStore((state) => state);
  const [checking, setChecking] = useState(true);

  const googleUser = useMemo(() => {
    if (!token) return null;
    try {
      const payload = decodeJwtPayload(token);
      return {
        email: (payload.email as string) || "",
        firstName: (payload.given_name as string) || "",
      };
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(`${BASE_URL}/auth/token-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (body?.hash) {
          localStorage.setItem("opt-token", JSON.stringify(body.hash));
          window.location.href = "/role";
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        setChecking(false);
      });
  }, [token]);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<GoogleSignupFormValues>({
    mode: "onChange",
    defaultValues: {
      email: "",
      firstName: "",
      companyName: "",
      terms: false,
    },
  });

  if (!token || !googleUser) {
    return <Navigate to={routesMap.auth.login.path} replace />;
  }

  if (checking) {
    return (
      <>
        <h2>
          Sign up to <OpticksLogo />
        </h2>
        <OpticksSpinner />
      </>
    );
  }

  const doSignup = (values: GoogleSignupFormValues) => {
    setLoading(true);
    const timezone = getTimezone();
    doTrialSignup({
      email: values.email,
      firstName: values.firstName,
      companyName: values.companyName,
      password: null,
      terms: values.terms,
      timezone,
      referrer,
      idToken: token,
      context: {
        pageUri: window.location.href,
        pageName: "Google Register",
      },
      hitId: window.optHitId,
      sid: document.cookie
        .split("; ")
        .find((row) => row.startsWith("opticksid="))
        ?.replace("opticksid=", ""),
    });
  };

  return (
    <>
      <h2>
        Sign up to <OpticksLogo />
      </h2>
      <p className="google-signup-subtitle">
        Complete your name and company to finish setting up your{" "}
        <OpticksPlanPillIndicator planType={PLAN_MODE.FREE} height={20} />{" "}
        account.
      </p>
      <form
        className="auth-form"
        noValidate={true}
        onSubmit={handleSubmit(doSignup)}
      >
        <div className="fields">
          <Controller
            name="email"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Email"
                  type="email"
                  required
                  surface="white"
                  input={field}
                  readOnly
                />
              </div>
            )}
          />

          <Controller
            name="firstName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="First name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                />
                {errors.firstName && (
                  <span className="form-invalid-message">
                    {errors.firstName.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="companyName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Company name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                />
                {errors.companyName && (
                  <span className="form-invalid-message">
                    {errors.companyName.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="terms"
            control={control}
            rules={{
              validate: (value) => value === true || "Required",
            }}
            render={({ field }) => (
              <div>
                <OpticksCheckbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  <span>
                    I accept the{" "}
                    <OpticksLink
                      href="https://optickssecurity.com/resources/files/Opticks-Terms-and-Conditions-for-Self-Service-v3.pdf"
                      label="terms and conditions"
                    />
                  </span>
                </OpticksCheckbox>

                {errors.terms && (
                  <span className="form-invalid-message">
                    {errors.terms.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <div className="login-btn">
          <OpticksButton
            type="submit"
            size="m-wide"
            disabled={!isValid || isSubmitting}
            label="Complete Sign Up"
            loading={isLoading}
          />
        </div>

        <div className="login-account">
          Already have an account?{" "}
          <Link to={routesMap.auth.login.path}>Login</Link>
        </div>
      </form>
    </>
  );
};

export default GoogleSignup;
